import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { listPublicSlots, submitBookingRequest } from "@/lib/booking.functions";
import { BOOKING_TOPICS, formatSlotRange } from "@/lib/booking.shared";
import { trackForm } from "@/lib/analytics";
import { FormPrivacyNotice, RequiredFieldHint } from "@/components/site/FormPrivacyNotice";

const inputClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

const FORM_ID = "slot-booking";

type Locale = "en" | "tr";

const COPY = {
  en: {
    dateLocale: "en-GB",
    doneTitle: "Your meeting slot is reserved.",
    doneBody: "We sent a confirmation to your email address and will follow up with the meeting link.",
    reference: "Your reference:",
    title: "Pick a slot with our team",
    intro:
      "Choose one of our open consultation slots. You get an email confirmation with a reference straight away.",
    availableSlots: "Available slots",
    loadingSlots: "Loading slots…",
    noSlots:
      "No open slots right now — please use the enquiry form above or the Outlook booking widget below.",
    seat: "seat left",
    seats: "seats left",
    name: "Name",
    company: "Company",
    email: "Email",
    topic: "Topic",
    message: "What would you like to discuss?",
    website: "Website",
    submit: "Reserve this slot",
    errChooseSlot: "Please choose a time slot.",
    errFields: "Please enter your name and a valid email address.",
    okToast: "Your slot is reserved.",
    errToast: "Booking failed. Please email info@oxory.net.",
    topics: {
      "SAP project staffing": "SAP project staffing",
      "Application Management Services": "Application Management Services",
      "Technology consulting": "Technology consulting",
      "Nearshore / partner network": "Nearshore / partner network",
      "AI & automation": "AI & automation",
      Other: "Other",
    } as Record<string, string>,
  },
  tr: {
    dateLocale: "tr-TR",
    doneTitle: "Görüşme saatiniz ayrıldı.",
    doneBody:
      "E-posta adresinize bir onay gönderdik; toplantı bağlantısını kısa süre içinde ileteceğiz.",
    reference: "Referans numaranız:",
    title: "Ekibimizle bir görüşme saati seçin",
    intro:
      "Açık danışmanlık saatlerimizden birini seçin. Referans numaralı e-posta onayını hemen alırsınız.",
    availableSlots: "Uygun saatler",
    loadingSlots: "Saatler yükleniyor…",
    noSlots:
      "Şu anda açık bir saat yok — lütfen yukarıdaki iletişim formunu veya aşağıdaki Outlook randevu takvimini kullanın.",
    seat: "kişilik yer kaldı",
    seats: "kişilik yer kaldı",
    name: "Ad Soyad",
    company: "Şirket",
    email: "E-posta",
    topic: "Konu",
    message: "Neyi görüşmek istersiniz?",
    website: "Web sitesi",
    submit: "Bu saati ayır",
    errChooseSlot: "Lütfen bir görüşme saati seçin.",
    errFields: "Lütfen adınızı ve geçerli bir e-posta adresi girin.",
    okToast: "Görüşme saatiniz ayrıldı.",
    errToast: "Rezervasyon başarısız oldu. Lütfen info@oxory.net adresine yazın.",
    topics: {
      "SAP project staffing": "SAP proje kaynak sağlama",
      "Application Management Services": "Uygulama yönetim hizmetleri (AMS)",
      "Technology consulting": "Teknoloji danışmanlığı",
      "Nearshore / partner network": "Nearshore / uzman ağı",
      "AI & automation": "Yapay zekâ ve otomasyon",
      Other: "Diğer",
    } as Record<string, string>,
  },
} as const;

/**
 * Slot-based meeting booking. Slots are maintained by the team in the admin
 * dashboard; every booking becomes a ticket there.
 */
export function SlotBookingForm({ locale = "en" }: { locale?: Locale }) {
  const t = COPY[locale];
  const fetchSlots = useServerFn(listPublicSlots);
  const submit = useServerFn(submitBookingRequest);
  const slotsQuery = useQuery({
    queryKey: ["public-booking-slots"],
    queryFn: () => fetchSlots({ data: undefined }),
  });

  const [slotId, setSlotId] = useState("");
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [topic, setTopic] = useState<string>(BOOKING_TOPICS[0]);
  const [honeypot, setHoneypot] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<{ ticketRef: string | null; slotLabel: string } | null>(null);

  const slots = slotsQuery.data ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const { toast } = await import("sonner");
    if (!slotId) {
      toast.error(t.errChooseSlot);
      return;
    }
    if (form.name.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      trackForm("invalid", FORM_ID, "Book a slot", topic);
      toast.error(t.errFields);
      return;
    }

    trackForm("submitted", FORM_ID, "Book a slot", topic);
    setSending(true);
    try {
      const result = await submit({
        data: {
          slotId,
          name: form.name,
          company: form.company,
          email: form.email,
          message: form.message,
          topic,
          sourcePath: typeof window === "undefined" ? "" : window.location.pathname,
          honeypot,
          startedAt,
        },
      });
      if (result?.ok) {
        const slot = slots.find((s) => s.id === slotId);
        trackForm("success", FORM_ID, "Book a slot", topic);
        toast.success(t.okToast);
        setDone({
          ticketRef: result.ticketRef ?? null,
          slotLabel: slot ? formatSlotRange(slot.starts_at, slot.ends_at, t.dateLocale) : "",
        });
        void slotsQuery.refetch();
      } else {
        trackForm("error", FORM_ID, "Book a slot", topic);
        toast.error(result?.message ?? t.errToast);
        void slotsQuery.refetch();
      }
    } catch (error) {
      console.error("[booking] submit failed:", error);
      trackForm("error", FORM_ID, "Book a slot", "network");
      toast.error(t.errToast);
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-8 shadow-card"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="size-10 text-highlight" aria-hidden="true" />
        <h3 className="mt-4 font-display text-xl font-bold">{t.doneTitle}</h3>
        {done.slotLabel && <p className="mt-2 text-sm font-semibold">{done.slotLabel}</p>}
        <p className="mt-3 text-sm text-muted-foreground">{t.doneBody}</p>
        {done.ticketRef && (
          <p className="mt-4 text-sm font-semibold">
            {t.reference}{" "}
            <span className="rounded bg-muted px-2 py-1 font-mono text-sm">{done.ticketRef}</span>
          </p>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="rounded-lg border border-border bg-card p-8 shadow-card"
      onFocus={() => trackForm("started", FORM_ID, "Book a slot", topic)}
    >
      <h3 className="font-display text-xl font-bold">{t.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{t.intro}</p>
      <RequiredFieldHint locale={locale} />

      <fieldset className="mt-6">
        <legend className="text-sm font-bold">{t.availableSlots} <span className="text-destructive">*</span></legend>
        {slotsQuery.isLoading && (
          <p className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" /> {t.loadingSlots}
          </p>
        )}
        {!slotsQuery.isLoading && slots.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">{t.noSlots}</p>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {slots.map((slot) => (
            <label
              key={slot.id}
              className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm ${
                slotId === slot.id ? "border-ring bg-secondary font-semibold" : "border-border"
              }`}
            >
              <input
                type="radio"
                name="slot"
                value={slot.id}
                checked={slotId === slot.id}
                onChange={() => setSlotId(slot.id)}
                className="size-4"
              />
              <span>
                {formatSlotRange(slot.starts_at, slot.ends_at, t.dateLocale)}
                <span className="ml-2 text-xs text-muted-foreground">
                  {slot.seatsLeft} {slot.seatsLeft === 1 ? t.seat : t.seats}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-bold">
          {t.name} <span className="text-destructive">*</span>
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>
        <label className="block text-sm font-bold">
          {t.company}
          <input
            className={inputClass}
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </label>
        <label className="block text-sm font-bold">
          {t.email} <span className="text-destructive">*</span>
          <input
            type="email"
            className={inputClass}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </label>
        <label className="block text-sm font-bold">
          {t.topic}
          <select className={inputClass} value={topic} onChange={(e) => setTopic(e.target.value)}>
            {BOOKING_TOPICS.map((topicValue) => (
              <option key={topicValue} value={topicValue}>
                {t.topics[topicValue] ?? topicValue}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-4 block text-sm font-bold">
        {t.message}
        <textarea
          className={inputClass}
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
      </label>

      {/* Honeypot — hidden from users, filled by bots. */}
      <label className="sr-only" aria-hidden="true">
        {t.website}
        <input
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </label>

      <FormPrivacyNotice locale={locale} />

      <button
        type="submit"
        disabled={sending || slots.length === 0}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground disabled:opacity-60"
      >
        {sending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          <CalendarCheck className="size-4" aria-hidden="true" />
        )}
        {t.submit}
      </button>
    </form>
  );
}
